/**
 * Typed client for the school-backend (FastAPI) /api/school/* endpoints.
 *
 * All requests go through the vite proxy (services/school-ui/vite.config.ts
 * routes /api/school/* → SCHOOL_BACKEND_URL, default http://localhost:8001).
 * The proxy injects X-User-Id + X-User-Role from SCHOOL_UI_DEV_USER_ID /
 * SCHOOL_UI_DEV_USER_ROLE env vars so we don't have to thread auth here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ────────────────────────────────────────────────────────────────────────────
// Response types — mirror app/schemas/student.py + app/schemas/classroom.py
// ────────────────────────────────────────────────────────────────────────────

export interface EnglishProfile {
  listening: number | null;
  speaking: number | null;
  reading: number | null;
  writing: number | null;
  vocabulary: number | null;
  grammar: number | null;
  overall: number | null;
}

export interface Student {
  id: string;
  school_id: string;
  student_code: string | null;
  name_zh: string;
  name_en: string | null;
  grade: string;
  section: string | null;
  enrollment_date: string | null;
  status: string;
  status_reason?: string | null;
  effective_date?: string | null;
  expected_return_date?: string | null;
  transferred_to?: string | null;
  english_profile: EnglishProfile;
  /** 学科分层:{subject_id: "advanced"|"intermediate"|"basic"} */
  subject_levels?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

/** 学科 slug;迁移 040 起不再限于三科(课程班级如 七年级1班·地理)。 */
export type Subject = string;

export interface Class {
  id: string;
  school_id: string;
  name: string;
  grade: string;
  section: string | null;
  subject: Subject;
  /** Legacy single-teacher pointer kept by school_classes migration 008.
   *  New code should prefer `teacher_ids` — the authoritative list from
   *  migration 018's school_class_teachers join. */
  teacher_id: string | null;
  /** All teachers assigned to this class (equal-teachers model — every
   *  entry has the same rights against the class). Defaults to []. */
  teacher_ids: string[];
  semester: string;
  created_at: string;
}

export interface ClassTeacherInfo {
  teacher_id: string;
  name: string;
  email: string | null;
  role: "teacher" | "homeroom";
  subjects: string[];
}

export interface ClassWithRoster extends Class {
  teachers: ClassTeacherInfo[];
  students: Student[];
  student_count: number;
}

export interface RadarAxis {
  skill: string;
  score: number | null;
}

export interface RadarData {
  axes: RadarAxis[];
  overall: number | null;
}

// ── Assignments ────────────────────────────────────────────────────────────

export type AssignmentType = "homework" | "in_class" | "project" | "presentation";

export interface Assignment {
  id: string;
  class_id: string;
  teacher_id: string;
  title: string;
  type: AssignmentType;
  assigned_date: string;
  due_date: string | null;
  max_score: number;
  knowledge_tags: string[];
  created_at: string;
}

export interface AssignmentScore {
  id: string;
  student_id: string;
  score: number | null;
  teacher_note: string | null;
  submitted_at: string | null;
  created_at: string;
}

export interface AssignmentWithScores extends Assignment {
  scores: AssignmentScore[];
}

export interface ScoreEntry {
  student_id: string;
  score: number | null;
  teacher_note?: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Low-level fetch — includes the OIDC bearer on every call so the edge
// nginx can pass it through to school-backend, which validates against
// Nova OS /oauth/userinfo. In local dev the vite proxy injects
// X-User-Id headers instead and the bearer (if any) is ignored.
// ────────────────────────────────────────────────────────────────────────────

const TOKEN_STORAGE_KEY = "nova_token";

export function authHeaders(): Record<string, string> {
  // Read directly from sessionStorage rather than React context so this
  // module stays hooks-free and reusable from non-component code.
  const t =
    typeof window !== "undefined"
      ? sessionStorage.getItem(TOKEN_STORAGE_KEY)
      : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function getJson<T>(path: string): Promise<T> {
  const resp = await fetch(path, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (!resp.ok) {
    const body = await resp.text().catch(() => "");
    throw new Error(`GET ${path} → ${resp.status}: ${body}`);
  }
  return (await resp.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`POST ${path} → ${resp.status}: ${text}`);
  }
  // 204 (batch score submit) has no body — guard against JSON parse.
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(path, {
    method: "PUT",
    credentials: "include",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`PUT ${path} → ${resp.status}: ${text}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

// ────────────────────────────────────────────────────────────────────────────
// React Query hooks
// ────────────────────────────────────────────────────────────────────────────

export function useClasses(params?: { mine?: boolean; semester?: string; school_id?: string }) {
  const search = new URLSearchParams();
  if (params?.mine) search.set("mine", "true");
  if (params?.semester) search.set("semester", params.semester);
  if (params?.school_id) search.set("school_id", params.school_id);
  const qs = search.toString();
  return useQuery({
    queryKey: ["school", "classes", params ?? {}],
    queryFn: () => getJson<Class[]>(`/api/school/classes${qs ? "?" + qs : ""}`),
  });
}

// ── Admin: school, teachers, create class ─────────────────────────────────

export interface SchoolInfo {
  id: string;
  name: string;
  code: string;
}

export interface EligibleTeacher {
  user_id: string;       // nova_users.id (OIDC sub)
  email: string;
  display_name: string;
  role: string;          // 'employee' | 'manager' | 'admin'
  teacher_id: string | null; // school_teachers.id if already registered
}

export function useSchool() {
  return useQuery({
    queryKey: ["admin", "school"],
    queryFn: () => getJson<SchoolInfo>("/api/admin/school"),
  });
}

export function useEligibleTeachers(enabled: boolean = true) {
  return useQuery({
    queryKey: ["admin", "teachers", "eligible"],
    queryFn: () => getJson<EligibleTeacher[]>("/api/admin/teachers/eligible"),
    // 普通老师无此端点权限;非管理视图传 enabled=false,避免每次进
    // 「我的班级」都白打一发 403(错误扫描降噪,2026-08-11)。
    enabled,
  });
}

export function useRegisterTeacher() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      postJson<{ teacher_id: string; created: boolean }>(
        "/api/admin/teachers",
        { nova_user_id: userId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teachers", "eligible"] });
    },
  });
}

export interface ClassCreatePayload {
  school_id: string;
  name: string;
  grade: string;
  section: string | null;
  /** 学科不再在建班时选择(跟老师走);留可选位兼容旧调用。 */
  subject?: Subject;
  /** Legacy single-teacher field. Send teacher_ids on new code; the
   *  backend prefers it when both are supplied and still honours
   *  teacher_id for older clients. */
  teacher_id?: string | null;
  teacher_ids?: string[];
  semester: string;
}

export function useReplaceClassTeachers(classId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teacher_ids: string[]) =>
      fetch(`/api/school/classes/${classId}/teachers`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ teacher_ids }),
      }).then((r) => {
        if (!r.ok) throw new Error(`PUT teachers ${r.status}`);
        return r.json() as Promise<Class>;
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["school", "classes"] });
      if (classId) {
        qc.invalidateQueries({ queryKey: ["school", "class", classId] });
      }
    },
  });
}

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClassCreatePayload) =>
      postJson<Class>("/api/school/classes", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["school", "classes"] });
    },
  });
}

export interface ClassUpdatePayload {
  name?: string;
  grade?: string;
  section?: string | null;
}

/** PATCH /api/school/classes/:id — rename / re-grade. Principal, group
 *  admin or the class's homeroom teacher (2026-09-13). Invalidates every
 *  place a class name is displayed: teacher class list, class detail, and
 *  the school-admin tabs (classes + teachers' class columns). */
export function useUpdateClass(classId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ClassUpdatePayload) =>
      fetch(`/api/school/classes/${classId}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => null);
          throw new Error(body?.detail ?? `PATCH class ${r.status}`);
        }
        return r.json() as Promise<Class>;
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["school", "classes"] });
      qc.invalidateQueries({ queryKey: ["admin", "school"] });
      if (classId) qc.invalidateQueries({ queryKey: ["school", "class", classId] });
    },
  });
}

export function useSetClassTeacherRole(classId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teacherId, role }: { teacherId: string; role: "teacher" | "homeroom" }) =>
      fetch(`/api/school/classes/${classId}/teachers/${teacherId}/role`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      }).then((r) => {
        if (!r.ok) throw new Error(`PUT role ${r.status}`);
      }),
    onSuccess: () => {
      if (classId) qc.invalidateQueries({ queryKey: ["school", "class", classId] });
    },
  });
}

export function useClassWithRoster(classId: string | undefined) {
  return useQuery({
    queryKey: ["school", "class", classId],
    queryFn: () => getJson<ClassWithRoster>(`/api/school/classes/${classId}`),
    enabled: !!classId,
  });
}

export function useStudent(studentId: string | undefined) {
  return useQuery({
    queryKey: ["school", "student", studentId],
    queryFn: () => getJson<Student>(`/api/school/students/${studentId}`),
    enabled: !!studentId,
  });
}

/** PUT /students/{id} for partial student updates. Today wired for the
 *  active/inactive status toggle on the roster (per Jarod's demo
 *  feedback). The backend already accepts any StudentUpdate field, so
 *  future name/grade edits hang off the same hook. */
export function useUpdateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{
        student_code: string | null;
        name_zh: string;
        name_en: string | null;
        grade: string;
        section: string | null;
        enrollment_date: string | null;
        status: string;
      }>;
    }) =>
      fetch(`/api/school/students/${id}`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      }).then((r) => {
        if (!r.ok) throw new Error(`PUT student ${r.status}`);
        return r.json() as Promise<Student>;
      }),
    onSuccess: (s) => {
      qc.setQueryData(["school", "student", s.id], s);
      qc.invalidateQueries({ queryKey: ["school", "classes"] });
      qc.invalidateQueries({ queryKey: ["school", "students"] });
    },
  });
}

/** All students in a school. Used by the AddStudentsModal to pick from
 *  the school-wide roster; the modal filters out students already in
 *  the target class on the frontend. */
export function useSchoolStudents(schoolId: string | undefined) {
  return useQuery({
    queryKey: ["school", "students", schoolId],
    queryFn: () =>
      getJson<Student[]>(`/api/school/students?school_id=${schoolId}`),
    enabled: !!schoolId,
  });
}

/** Create a brand-new student row. Used by the AddStudentsModal's
 *  inline "new student" form when the admin needs to add someone who
 *  isn't yet in the school roster (e.g. a transfer mid-semester, ahead
 *  of a full admissions interview). Invalidates the school-wide
 *  students cache so the new row shows up in future pickers. */
export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      school_id: string;
      name_zh: string;
      name_en?: string | null;
      grade: string;
      section?: string | null;
      student_code?: string | null;
    }) => postJson<Student>("/api/school/students", payload),
    onSuccess: (created) => {
      qc.invalidateQueries({
        queryKey: ["school", "students", created.school_id],
      });
    },
  });
}

/** Enroll one or more existing students into a class. Idempotent — the
 *  backend uses ON CONFLICT DO NOTHING on (class_id, student_id). */
export function useEnrollStudents(classId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (studentIds: string[]) =>
      postJson<void>(`/api/school/classes/${classId}/students`, {
        student_ids: studentIds,
      }),
    onSuccess: () => {
      // Refresh the class roster + stats so the new students appear
      // without a manual reload.
      qc.invalidateQueries({ queryKey: ["school", "class", classId] });
      qc.invalidateQueries({ queryKey: ["school", "stats", "class", classId] });
      // Bust the admissions pools too — enroll auto-promotes any
      // 'accepted' student to 'active', so the 录取候补 row vanishes.
      qc.invalidateQueries({ queryKey: ["school", "admissions"] });
    },
  });
}

export function useStudentRadar(
  studentId: string | undefined,
  subject: Subject = "english",
) {
  return useQuery({
    queryKey: ["school", "student", studentId, "radar", subject],
    queryFn: () =>
      getJson<RadarData>(`/api/school/students/${studentId}/radar?subject=${subject}`),
    enabled: !!studentId,
  });
}

// ── Assignment hooks ──────────────────────────────────────────────────────

export function useAssignments(classId: string | undefined) {
  return useQuery({
    queryKey: ["school", "assignments", classId],
    queryFn: () =>
      getJson<Assignment[]>(`/api/school/assignments?class_id=${classId}`),
    enabled: !!classId,
  });
}

export function useAssignmentDetail(assignmentId: string | undefined) {
  return useQuery({
    queryKey: ["school", "assignment", assignmentId],
    queryFn: () =>
      getJson<AssignmentWithScores>(`/api/school/assignments/${assignmentId}`),
    enabled: !!assignmentId,
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      class_id: string;
      teacher_id: string;
      title: string;
      type: AssignmentType;
      assigned_date: string;
      due_date?: string | null;
      max_score: number;
      knowledge_tags?: string[];
    }) => postJson<Assignment>("/api/school/assignments", payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["school", "assignments", vars.class_id] });
    },
  });
}

export interface AssignmentUpdatePayload {
  title?: string;
  type?: AssignmentType;
  assigned_date?: string;
  due_date?: string | null;
  max_score?: number;
  knowledge_tags?: string[];
}

/** PATCH /api/school/assignments/:id (2026-09-14). classId is only used
 *  to bust the list the row lives in. */
export function useUpdateAssignment(assignmentId: string, classId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignmentUpdatePayload) =>
      fetch(`/api/school/assignments/${assignmentId}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => null);
          throw new Error(body?.detail ?? `PATCH assignment ${r.status}`);
        }
        return r.json() as Promise<Assignment>;
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["school", "assignments", classId] });
      qc.invalidateQueries({ queryKey: ["school", "assignment", assignmentId] });
    },
  });
}

/** GET /api/school/assignments/:id/scope — taught knowledge points for the
 *  homework 自定义 mode (2026-09-15). Same shape as the exam blueprint's
 *  taught_scope so the picker looks and behaves the same. */
export interface HomeworkScope {
  subject_slug: string;
  subject_id: string | null;
  taught_scope: TaughtKp[];
}

export function useHomeworkScope(assignmentId: string | undefined, subjectSlug: string | undefined, enabled: boolean) {
  const qs = subjectSlug ? `?subject_slug=${encodeURIComponent(subjectSlug)}` : "";
  return useQuery({
    queryKey: ["school", "assignment", assignmentId, "scope", subjectSlug ?? "auto"],
    queryFn: () => getJson<HomeworkScope>(`/api/school/assignments/${assignmentId}/scope${qs}`),
    enabled: enabled && !!assignmentId,
  });
}

export function useSubmitScores(assignmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scores: ScoreEntry[]) =>
      postJson<void>(`/api/school/assignments/${assignmentId}/scores`, { scores }),
    onSuccess: (_data, scores) => {
      // Bust caches for the assignment detail AND every student whose
      // profile/radar might have changed.
      qc.invalidateQueries({ queryKey: ["school", "assignment", assignmentId] });
      for (const s of scores) {
        qc.invalidateQueries({ queryKey: ["school", "student", s.student_id] });
      }
    },
  });
}

// ── Admissions ────────────────────────────────────────────────────────────

export interface Admission {
  id: string;
  student_id: string;
  interviewer_id: string | null;
  interview_date: string;
  speaking_score: number | null;
  reading_score: number | null;
  writing_score: number | null;
  listening_score: number | null;
  vocabulary_score: number | null;
  overall_score: number | null;
  interviewer_notes: string | null;
  ai_assessment: Record<string, unknown>;
  interview_transcript: InterviewTranscript;
  created_at: string;
}

// ── Admissions funnel — adaptive interview shapes ────────────────────────

export type SkillAxis =
  | "listening" | "speaking" | "reading" | "writing" | "vocabulary" | "grammar";

export type QuickRating = "fluent" | "ok" | "struggling";

export interface InterviewQuestionSlot {
  axis: SkillAxis;
  question: string;
  difficulty?: "easier" | "same" | "harder";
  student_answer: string | null;
  interviewer_quick_rating: QuickRating | null;
}

export interface InterviewRound {
  // N-round design — no upper bound. The teacher decides when to stop.
  round: number;
  questions: InterviewQuestionSlot[];
}

export interface InterviewWrittenTestAttachment {
  filename: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
}

export interface InterviewTranscript {
  rounds?: InterviewRound[];
  written_test_attachments?: InterviewWrittenTestAttachment[];
  final_per_axis_scores?: Record<SkillAxis, number>;
  ai_recommendation?: "accept" | "reject" | "borderline" | null;
  interviewer_decision?: "accept" | "reject" | null;
  decided_at?: string;
  decision_note?: string;
}

export interface InterviewState {
  admission_id: string;
  student_id: string;
  current_round: number;
  pending_questions: { axis: SkillAxis; question: string }[];
  interim_per_axis_scores: Record<SkillAxis, number | null>;
  is_round_complete: boolean;
  can_finalize: boolean;
  can_generate_next_round: boolean;
  is_complete: boolean;
  transcript: InterviewTranscript;
}

export interface Candidate {
  id: string;
  school_id: string;
  student_code: string | null;
  name_zh: string;
  name_en: string | null;
  grade: string;
  section: string | null;
  status: string;
  latest_admission_id: string | null;
  latest_interview_date: string | null;
  latest_overall_score: number | null;
  latest_interview_round: number | null;
}

export function useStudentAdmission(studentId: string | undefined) {
  return useQuery({
    queryKey: ["school", "student", studentId, "admission"],
    queryFn: async () => {
      const resp = await fetch(`/api/school/admissions/${studentId}`, {
        credentials: "include",
        headers: authHeaders(),
      });
      if (resp.status === 404) return null; // not yet interviewed
      if (!resp.ok) throw new Error(`GET admission → ${resp.status}`);
      return (await resp.json()) as Admission;
    },
    enabled: !!studentId,
  });
}

export function useCreateAdmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      student_id: string;
      interview_date: string;
      speaking_score?: number | null;
      reading_score?: number | null;
      writing_score?: number | null;
      listening_score?: number | null;
      vocabulary_score?: number | null;
      overall_score?: number | null;
      interviewer_notes?: string | null;
      custom_prompt?: string | null;
    }) => postJson<Admission>("/api/school/admissions", payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["school", "student", vars.student_id] });
    },
  });
}

// ── Admissions funnel — pools, interview, decision ───────────────────────

/** 招生候选 — pre-decision pool (candidate + interviewed). */
export function useCandidates(schoolId: string | undefined) {
  return useQuery({
    queryKey: ["school", "admissions", "candidates", schoolId],
    queryFn: () =>
      getJson<Candidate[]>(`/api/school/admissions/candidates?school_id=${schoolId}`),
    enabled: !!schoolId,
  });
}

/** 录取候补 — accepted, awaiting class placement. */
export function useAccepted(schoolId: string | undefined) {
  return useQuery({
    queryKey: ["school", "admissions", "accepted", schoolId],
    queryFn: () =>
      getJson<Candidate[]>(`/api/school/admissions/accepted?school_id=${schoolId}`),
    enabled: !!schoolId,
  });
}

/** Start (or resume) the adaptive interview for a candidate.
 *  Idempotent on student_id — server returns existing admission +
 *  current state if one is in-flight. */
export function useStartInterview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { student_id: string; interview_date?: string }) =>
      postJson<InterviewState>("/api/school/admissions/interview/start", payload),
    onSuccess: (state) => {
      qc.invalidateQueries({ queryKey: ["school", "admissions"] });
      qc.invalidateQueries({ queryKey: ["school", "student", state.student_id] });
    },
  });
}

export function useInterviewState(admissionId: string | undefined) {
  return useQuery({
    queryKey: ["school", "admissions", "interview", admissionId],
    queryFn: () =>
      getJson<InterviewState>(`/api/school/admissions/interview/${admissionId}`),
    enabled: !!admissionId,
  });
}

export function useSubmitInterviewAnswer(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      round: number;
      axis: SkillAxis;
      student_answer: string;
      interviewer_quick_rating: QuickRating;
    }) =>
      postJson<InterviewState>(
        `/api/school/admissions/interview/${admissionId}/answer`,
        payload,
      ),
    onSuccess: (state) => {
      // Mutation returns the new state — write it into the cache so the
      // page re-renders without a follow-up GET. Also bump the candidate
      // pool in case the round_index moved.
      qc.setQueryData(
        ["school", "admissions", "interview", admissionId],
        state,
      );
      qc.invalidateQueries({ queryKey: ["school", "admissions", "candidates"] });
    },
  });
}

/** N-round design: teacher chooses to add another 6 questions. The
 *  backend gates this on "current round fully answered" and there's no
 *  upper cap — teacher stops when they have enough signal. */
export function useGenerateNextInterviewRound(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postJson<InterviewState>(
        `/api/school/admissions/interview/${admissionId}/next-round`,
        {},
      ),
    onSuccess: (state) => {
      qc.setQueryData(
        ["school", "admissions", "interview", admissionId],
        state,
      );
    },
  });
}

/** Upload a written-test file (photo or PDF of the marked paper).
 *  Multipart POST — file is stored on school-backend's blob volume +
 *  recorded in transcript.written_test_attachments. Returns updated
 *  state so the row appears immediately. */
export function useUploadInterviewAttachment(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return fetch(
        `/api/school/admissions/interview/${admissionId}/attachments`,
        {
          method: "POST",
          headers: authHeaders(),
          body: fd,
        },
      ).then((r) => {
        if (!r.ok) throw new Error(`upload ${r.status}`);
        return r.json() as Promise<InterviewState>;
      });
    },
    onSuccess: (state) => {
      qc.setQueryData(
        ["school", "admissions", "interview", admissionId],
        state,
      );
    },
  });
}

export function useDeleteInterviewAttachment(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (index: number) =>
      fetch(
        `/api/school/admissions/interview/${admissionId}/attachments/${index}`,
        {
          method: "DELETE",
          headers: authHeaders(),
        },
      ).then((r) => {
        if (!r.ok) throw new Error(`delete ${r.status}`);
        return r.json() as Promise<InterviewState>;
      }),
    onSuccess: (state) => {
      qc.setQueryData(
        ["school", "admissions", "interview", admissionId],
        state,
      );
    },
  });
}

/** Teacher chose to stop. Backend fires AI scoring (idempotent) and
 *  flips is_complete so the FinalizePanel renders. */
export function usePrepareFinalizeInterview(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postJson<InterviewState>(
        `/api/school/admissions/interview/${admissionId}/prepare-finalize`,
        {},
      ),
    onSuccess: (state) => {
      qc.setQueryData(
        ["school", "admissions", "interview", admissionId],
        state,
      );
    },
  });
}

export function useFinalizeInterview(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      per_axis_scores: Record<SkillAxis, number>;
      overall_score: number;
      interviewer_notes?: string | null;
      ai_recommendation?: "accept" | "reject" | "borderline" | null;
    }) =>
      postJson<Admission>(
        `/api/school/admissions/interview/${admissionId}/finalize`,
        payload,
      ),
    onSuccess: (adm) => {
      qc.invalidateQueries({ queryKey: ["school", "admissions"] });
      qc.invalidateQueries({ queryKey: ["school", "student", adm.student_id] });
      qc.invalidateQueries({
        queryKey: ["school", "admissions", "interview", admissionId],
      });
    },
  });
}

export function useDecideAdmission(admissionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { decision: "accept" | "reject"; note?: string }) =>
      postJson<Admission>(
        `/api/school/admissions/${admissionId}/decision`,
        payload,
      ),
    onSuccess: (adm) => {
      qc.invalidateQueries({ queryKey: ["school", "admissions"] });
      qc.invalidateQueries({ queryKey: ["school", "student", adm.student_id] });
    },
  });
}

// ── Exams ─────────────────────────────────────────────────────────────────

export type ExamType = "quiz" | "unit_test" | "midterm" | "final" | "mock" | "standardized";

export interface ExamSection {
  name: string;       // Chinese label, e.g. "听力" / "句型翻译"
  name_en: string;    // stable key matching section_scores, e.g. "listening" / "translation"
  max_score: number;
}

export interface Exam {
  id: string;
  school_id: string;
  class_id: string | null;
  title: string;
  type: ExamType;
  exam_date: string;
  max_listening: number | null;
  max_reading: number | null;
  max_writing: number | null;
  max_speaking: number | null;
  max_vocabulary: number | null;
  max_grammar: number | null;
  max_total: number;
  // Jiangning-shape exams use this dynamic structure. Empty array = legacy
  // fixed-6-section exam created via the simple form path.
  sections: ExamSection[];
  created_at: string;
}

export interface ExamScoreRecord {
  id: string;
  student_id: string;
  listening: number | null;
  reading: number | null;
  writing: number | null;
  speaking: number | null;
  vocabulary: number | null;
  grammar: number | null;
  total: number;
  percentile: number | null;
  teacher_note: string | null;
  created_at: string;
}

export interface ExamWithScores extends Exam {
  scores: ExamScoreRecord[];
}

export interface ExamScoreEntry {
  student_id: string;
  listening?: number | null;
  reading?: number | null;
  writing?: number | null;
  speaking?: number | null;
  vocabulary?: number | null;
  grammar?: number | null;
  /** Dynamic-shape section scores keyed by sections[].name_en. */
  section_scores?: Record<string, number>;
  total: number;
  teacher_note?: string;
}

export function useExams(classId: string | undefined) {
  return useQuery({
    queryKey: ["school", "exams", classId],
    queryFn: () => getJson<Exam[]>(`/api/school/exams?class_id=${classId}`),
    enabled: !!classId,
  });
}

export function useDeleteExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (examId: string) =>
      fetch(`/api/school/exams/${examId}`, { method: "DELETE", headers: authHeaders() }).then((r) => {
        if (!r.ok) throw new Error(`DELETE exam ${r.status}`);
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["school", "exams"] });
    },
  });
}

export function useCreateExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      school_id: string;
      class_id: string | null;
      title: string;
      subject_id?: string | null;
      type: ExamType;
      exam_date: string;
      max_listening?: number | null;
      max_reading?: number | null;
      max_writing?: number | null;
      max_speaking?: number | null;
      max_vocabulary?: number | null;
      max_grammar?: number | null;
      max_total: number;
    }) => postJson<Exam>("/api/school/exams", payload),
    onSuccess: (_data, vars) => {
      if (vars.class_id) {
        qc.invalidateQueries({ queryKey: ["school", "exams", vars.class_id] });
      }
    },
  });
}

export function useSubmitExamScores(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scores: ExamScoreEntry[]) =>
      postJson<void>(`/api/school/exams/${examId}/scores`, { scores }),
    onSuccess: (_data, scores) => {
      qc.invalidateQueries({ queryKey: ["school", "exam", examId] });
      for (const s of scores) {
        qc.invalidateQueries({ queryKey: ["school", "student", s.student_id] });
      }
    },
  });
}

// ── iTarget ───────────────────────────────────────────────────────────────

export type AnswerStatus = "correct" | "incorrect" | "partial";

export interface ItargetQuestion {
  id: number;
  question: string;
  type: string;
  difficulty: "foundation" | "standard" | "challenge";
  skill: string;
  knowledge_point: string;
  correct_answer: string;
  /** 配图:agent 的图形说明 + 后端画好的内联 SVG(2026-09-20)。 */
  figure?: string | null;
  figure_svg?: string | null;
}

export interface ItargetGroupingEntry {
  student_id: string;
  student_name: string;
  diagnostic_score: number;
  reason: string;
}

export interface ItargetGroupings {
  foundation?: ItargetGroupingEntry[];
  standard?: ItargetGroupingEntry[];
  challenge?: ItargetGroupingEntry[];
  summary?: string;
}

export interface ItargetSession {
  id: string;
  class_id: string;
  teacher_id: string;
  lesson_topic: string;
  lesson_date: string;
  /** 学科 slug(2026-09-21 起落库;老记录可能为空)。 */
  subject?: string | null;
  questions: ItargetQuestion[];
  groupings: ItargetGroupings;
  status: "draft" | "questions_ready" | "answers_collected" | "grouped";
  created_at: string;
  /** Saved marks — only populated by the detail endpoint. */
  answers?: ItargetAnswerEntry[];
  /** 创建老师姓名(列表「本班全部」视图用)。 */
  teacher_name?: string | null;
}

export interface ItargetAnswerEntry {
  student_id: string;
  question_id: number;
  answer_status: AnswerStatus;
}

export function useGenerateItarget() {
  return useMutation({
    mutationFn: (payload: {
      class_id: string;
      teacher_id: string;
      lesson_topic: string;
      lesson_date: string;
      subject?: string;
      custom_prompt?: string | null;
    }) => postJson<ItargetSession>("/api/school/itarget/generate", payload),
  });
}

export function useSubmitItargetAnswers(sessionId: string) {
  return useMutation({
    mutationFn: (answers: ItargetAnswerEntry[]) =>
      postJson<void>(`/api/school/itarget/${sessionId}/answers`, { answers }),
  });
}

export function useGroupItarget(sessionId: string) {
  return useMutation({
    mutationFn: () => postJson<ItargetSession>(`/api/school/itarget/${sessionId}/group`, {}),
  });
}

/** mine=true(默认)只列本人建的记录;false = 本班所有老师的(2026-09-19)。 */
export function useItargetSessions(classId: string, mine = true) {
  return useQuery({
    queryKey: ["school", "itarget-sessions", classId, mine ? "mine" : "all"],
    queryFn: () => getJson<ItargetSession[]>(`/api/school/itarget/sessions?class_id=${classId}&mine=${mine}`),
    enabled: !!classId,
  });
}

/** Detail fetch on history-row click — the detail response carries saved
 *  answers so the Step-2 grid can be re-hydrated. */
export function fetchItargetSession(sessionId: string): Promise<ItargetSession> {
  return getJson<ItargetSession>(`/api/school/itarget/${sessionId}`);
}

// ── Stats + Reports ───────────────────────────────────────────────────────

export interface TimelinePoint {
  date: string;
  label: string;
  source: "admission" | "assignment" | "exam" | "itarget";
  value: number | null;
  detail: string;
}

export interface TrendPoint {
  date: string;
  score: number;
}

export interface StudentStats {
  points: TimelinePoint[];
  trend: TrendPoint[];
}

export interface ClassStats {
  class_id: string;
  class_name: string;
  student_count: number;
  class_average: number | null;
  skill_averages: Record<string, number | null>;
  distribution: {
    excellent_85_100: number;
    good_70_84: number;
    average_60_69: number;
    needs_improvement_0_59: number;
    not_assessed: number;
  };
}

export interface HeatmapRow {
  student_id: string;
  name_zh: string;
  name_en: string | null;
  skills: Record<string, number | null>;
  overall: number | null;
}

export interface ClassHeatmap {
  skills: string[];
  rows: HeatmapRow[];
}

export interface Report {
  id: string;
  school_id: string;
  report_type: string;
  target_type: "student" | "class";
  target_id: string;
  period: string | null;
  generated_by: string | null;
  content: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  summary: string | null;
  created_at: string;
}

export function useStudentStats(
  studentId: string | undefined,
  subject: Subject = "english",
) {
  return useQuery({
    queryKey: ["school", "stats", "student", studentId, subject],
    queryFn: () =>
      getJson<StudentStats>(
        `/api/school/stats/student/${studentId}?subject=${subject}`,
      ),
    enabled: !!studentId,
  });
}

export function useClassStats(classId: string | undefined) {
  return useQuery({
    queryKey: ["school", "stats", "class", classId],
    queryFn: () => getJson<ClassStats>(`/api/school/stats/class/${classId}`),
    enabled: !!classId,
  });
}

export function useClassHeatmap(classId: string | undefined) {
  return useQuery({
    queryKey: ["school", "stats", "class", classId, "heatmap"],
    queryFn: () => getJson<ClassHeatmap>(`/api/school/stats/class/${classId}/heatmap`),
    enabled: !!classId,
  });
}

export function useReports(targetId: string | undefined) {
  return useQuery({
    queryKey: ["school", "reports", targetId],
    queryFn: () =>
      getJson<Report[]>(
        targetId ? `/api/school/reports?target_id=${targetId}` : "/api/school/reports",
      ),
    enabled: !!targetId,
  });
}

export function useGenerateStudentReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      student_id: string;
      period?: string | null;
      custom_prompt?: string | null;
      locale?: string | null;
      /** 学科 slug;null/省略 = 综合报告(所有有数据的学科)。 */
      subject?: string | null;
    }) => postJson<Report>("/api/school/reports/student", payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["school", "reports", vars.student_id] });
    },
  });
}

/** PATCH a report's content + summary. Used by ReportCard's edit mode
 *  so teachers can refine the AI draft before sharing / printing. */
export function useUpdateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      content,
      summary,
    }: {
      id: string;
      content?: Record<string, unknown>;
      summary?: string | null;
    }) =>
      fetch(`/api/school/reports/${id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ content, summary }),
      }).then((r) => {
        if (!r.ok) throw new Error(`PATCH report ${r.status}`);
        return r.json() as Promise<Report>;
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["school", "reports"] });
      qc.invalidateQueries({
        queryKey: ["school", "reports", r.target_id],
      });
    },
  });
}

export function useGenerateClassReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      class_id: string;
      period?: string | null;
      custom_prompt?: string | null;
      locale?: string | null;
      /** 学科 slug;null/省略 = 综合报告(所有有数据的学科)。 */
      subject?: string | null;
    }) => postJson<Report>("/api/school/reports/class", payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["school", "reports", vars.class_id] });
    },
  });
}

// ── Extensions (Agent 5) ─────────────────────────────────────────────────

export interface ExtensionVocabNote {
  word: string;
  meaning_zh: string;
  example: string;
}

export interface ExtensionComprehensionQ {
  question: string;
  answer: string;
}

export interface ExtensionReading {
  title: string;
  text: string;
  word_count: number;
  difficulty: "A_level" | "B_level" | "mixed";
  vocabulary_notes?: ExtensionVocabNote[];
  comprehension_questions: ExtensionComprehensionQ[];
}

export interface ExtensionExercise {
  id: number;
  type: string;
  instruction: string;
  content: string;
  answer: string;
  target_skill: string;
  level: "A" | "B" | "both";
}

export interface ExtensionDiscussion {
  prompt_en: string;
  prompt_zh: string;
  level: "A" | "B" | "both";
  suggested_duration_minutes: number;
  teacher_notes?: string;
}

export interface ExtensionTask {
  task_name: string;
  description: string;
  expected_duration_minutes: number;
  output_format: string;
}

export interface ExtensionMaterials {
  unit: number;
  topic: string;
  extension_reading: ExtensionReading;
  practice_exercises: ExtensionExercise[];
  discussion_prompts: ExtensionDiscussion[];
  differentiated_tasks: {
    a_level: ExtensionTask;
    b_level: ExtensionTask;
  };
}

// ── Teaching plan v2 (2026-09-15, subject-neutral) ─────────────────────
export interface TeachingPlanLesson { period: number; focus: string; activities: string[]; materials?: string }
export interface TeachingPlan {
  week: number;
  topic: string;
  subject?: string;
  subject_name?: string;
  objectives: string[];
  knowledge_points: string[];
  lessons: TeachingPlanLesson[];
  exercises: { stem: string; answer: string }[];
  differentiation: { basic: string; advanced: string };
  homework: string;
  assessment: string;
  notes?: string;
}

export function useGenerateTeachingPlan() {
  return useMutation({
    mutationFn: (payload: {
      week: number;
      topic: string;
      subject_slug?: string;
      class_id?: string | null;
      syllabus_text?: string | null;
      custom_prompt?: string | null;
    }) => postJson<TeachingPlan>("/api/school/teaching-plans/generate", payload),
  });
}

/** POST /api/school/curriculum/units/from-syllabus — syllabus text → units + KPs. */
export function useUnitsFromSyllabus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { class_id: string; subject_id?: string; text: string; weeks?: number }) =>
      postJson<{ units_created: number; kps_created: string[]; kp_status: string; semester_summary?: string | null }>(
        "/api/school/curriculum/units/from-syllabus",
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["school", "curriculum"] });
      void qc.invalidateQueries({ queryKey: ["school", "kp"] });
    },
  });
}

export function useGenerateExtensions() {
  return useMutation({
    mutationFn: (payload: {
      unit: number;
      topic: string;
      grade?: string;
      custom_prompt?: string | null;
      subject?: string;
    }) => postJson<ExtensionMaterials>("/api/school/extensions/generate", payload),
  });
}

// ── Image upload (B3) ───────────────────────────────────────────────────

export interface VisionCandidate {
  student_id: string;
  score: number | null;
  question_scores?: Record<string, number> | null;
  notes: string;
  uncertain: boolean;
}

export interface VisionResponse {
  assignment_id: string;
  candidates: VisionCandidate[];
  roster_size: number;
  mock_mode: boolean;
}

export async function uploadScoreImage(
  assignmentId: string,
  file: File,
  customPrompt?: string,
): Promise<VisionResponse> {
  const form = new FormData();
  form.append("image", file);
  if (customPrompt) form.append("custom_prompt", customPrompt);
  const resp = await fetch(`/api/school/assignments/${assignmentId}/scores/from-image`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`upload → ${resp.status}: ${text}`);
  }
  return (await resp.json()) as VisionResponse;
}

export interface ExamVisionCandidate {
  student_id: string;
  section_scores: Record<string, number>;
  total: number;
  notes: string;
  uncertain: boolean;
}

export interface ExamVisionResponse {
  exam_id: string;
  candidates: ExamVisionCandidate[];
  roster_size: number;
  section_count: number;
  mock_mode: boolean;
}

export async function uploadExamScoreImage(
  examId: string,
  file: File,
  customPrompt?: string,
): Promise<ExamVisionResponse> {
  const form = new FormData();
  form.append("image", file);
  if (customPrompt) form.append("custom_prompt", customPrompt);
  const resp = await fetch(`/api/school/exams/${examId}/scores/from-image`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`upload → ${resp.status}: ${text}`);
  }
  return (await resp.json()) as ExamVisionResponse;
}

// ── Org P1: caller standing + schools CRUD (集团-学校 layer) ─────────────

export interface OrgMe {
  is_admin: boolean;
  school_id: string | null;
  school_role: "teacher" | "admin" | "admissions" | null;
  subjects?: { id: string; slug: string; name: string; role_type: string }[];
  modules?: Record<string, "visible" | "readonly" | "hidden">;
  data_use_acked?: boolean;
}

export function useOrgMe() {
  return useQuery({
    queryKey: ["admin", "org", "me"],
    queryFn: () => getJson<OrgMe>("/api/admin/org/me"),
    staleTime: 60_000,
  });
}

/** Group admin, or this school's principal (school_teachers.role='admin'). */
export function canManageSchool(me: OrgMe | undefined, schoolId?: string | null): boolean {
  if (!me) return false;
  if (me.is_admin) return true;
  if (me.school_role !== "admin") return false;
  return schoolId == null || me.school_id === schoolId;
}

export interface SchoolFull extends SchoolInfo {
  logo: string;
  status?: "active" | "archived";
}

export function useSchools(includeArchived = false) {
  return useQuery({
    queryKey: ["admin", "schools", includeArchived],
    queryFn: () =>
      getJson<SchoolFull[]>(`/api/admin/schools?include_archived=${includeArchived}`),
  });
}

export function useSaveSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (s: { id?: string; name: string; code: string; logo: string }) => {
      if (s.id) {
        const resp = await fetch(`/api/admin/schools/${s.id}`, {
          method: "PUT",
          credentials: "include",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(s),
        });
        if (!resp.ok) throw new Error(`PUT school → ${resp.status}`);
        return (await resp.json()) as SchoolFull;
      }
      return postJson<SchoolFull>("/api/admin/schools", s);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "schools"] });
      void qc.invalidateQueries({ queryKey: ["admin", "school"] });
    },
  });
}

export function useDeleteSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/admin/schools/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw Object.assign(new Error(`DELETE school ${resp.status}`), { detail: body?.detail });
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "schools"] }),
  });
}

export function useArchiveSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id: string; confirm_name: string }) => {
      const resp = await fetch(`/api/admin/schools/${p.id}/archive`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ confirm_name: p.confirm_name }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`archive ${resp.status}`), { detail: body?.detail });
      return body as { ok: boolean };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "schools"] }),
  });
}

export function useUnarchiveSchool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postJson<{ ok: boolean }>(`/api/admin/schools/${id}/unarchive`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "schools"] }),
  });
}

export function useTransferTeachers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      teacher_ids: string[]; to_school_id: string;
      class_reassignments: Record<string, string>; reason?: string;
    }) => {
      const resp = await fetch("/api/school/transfers/teachers", {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`teachers ${resp.status}`), { detail: body?.detail });
      return body as { batch_id: string; succeeded: number };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "school"] }),
  });
}

// ── Exam Engine P0: knowledge-point ontology review ──────────────────

export interface KnowledgePoint {
  id: string;
  school_id: string;
  subject_id: string;
  code: string;
  name_zh: string;
  name_en: string | null;
  parent_id: string | null;
  grade_band: string | null;
  status: "candidate" | "approved" | "merged" | "deprecated";
  merged_into: string | null;
  source: string;
  source_ref: string | null;
  /** 申请人(school_teachers.id / 姓名),候选知识点审核时显示(2026-09-19)。 */
  proposed_by?: string | null;
  proposed_by_name?: string | null;
}

export function useKnowledgePoints(status?: string, subjectId?: string | null) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  // 学科标签挂钩(Jarod 2026-08-14):蓝图补充知识点等场景按"考试的
  // 学科"取候选,不再依赖老师单学科推断(多学科账号会回落英语)。
  if (subjectId) params.set("subject_id", subjectId);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["school", "kp", status ?? "all", subjectId ?? "auto"],
    queryFn: () => getJson<KnowledgePoint[]>(`/api/school/kp${qs}`),
  });
}

function useKpMutation<T>(fn: (arg: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "kp"] }),
  });
}

export function useCreateKp() {
  return useKpMutation(
    (kp: { code: string; name_zh: string; name_en?: string; parent_id?: string | null; grade_band?: string; subject_id?: string }) =>
      postJson("/api/school/kp", kp),
  );
}

export function useUpdateKp() {
  return useKpMutation(
    async (kp: { id: string; code: string; name_zh: string; name_en?: string | null; parent_id?: string | null; grade_band?: string | null }) => {
      const resp = await fetch(`/api/school/kp/${kp.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(kp),
      });
      if (!resp.ok) throw new Error(`PUT kp → ${resp.status}`);
      return resp.json();
    },
  );
}

export function useApproveKp() {
  return useKpMutation((id: string) => postJson(`/api/school/kp/${id}/approve`, {}));
}

export function useMergeKp() {
  return useKpMutation((m: { id: string; into_id: string }) =>
    postJson(`/api/school/kp/${m.id}/merge`, { into_id: m.into_id }),
  );
}

export function useDeprecateKp() {
  return useKpMutation((id: string) => postJson(`/api/school/kp/${id}/deprecate`, {}));
}

export function useExtractKps() {
  return useKpMutation((p: { text: string; grade_band?: string; subject_id?: string }) =>
    postJson<{ inserted: number; skipped: number; total_returned: number }>("/api/school/kp/extract", p),
  );
}

// ── Exam Engine P0: teaching-progress calendar ───────────────────────

export interface UnitKp {
  kp_id: string;
  code: string;
  name_zh: string;
  emphasis: number;
  kp_status: string;
}

export interface CurriculumUnit {
  id: string;
  school_id: string;
  subject_id: string;
  class_id: string | null;
  grade: string;
  semester: string;
  week_no: number | null;
  planned_start: string | null;
  planned_end: string | null;
  actual_taught_date: string | null;
  title: string;
  xuean_stage: string | null;
  status: "planned" | "in_progress" | "taught" | "skipped";
  kps: UnitKp[];
}

export function useCurriculumUnits(classId: string | undefined, semester?: string, subjectId?: string) {
  const qs = new URLSearchParams();
  if (classId) qs.set("class_id", classId);
  if (semester) qs.set("semester", semester);
  if (subjectId) qs.set("subject_id", subjectId);
  return useQuery({
    queryKey: ["school", "curriculum", classId ?? "", semester ?? "", subjectId ?? ""],
    queryFn: () => getJson<CurriculumUnit[]>(`/api/school/curriculum/units?${qs}`),
    enabled: !!classId,
  });
}

function useUnitMutation<T>(fn: (arg: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "curriculum"] }),
  });
}

export interface UnitIn {
  class_id: string;
  title: string;
  week_no?: number | null;
  planned_start?: string | null;
  planned_end?: string | null;
  xuean_stage?: string | null;
  kp_ids?: string[];
}

export function useCreateUnit() {
  return useUnitMutation((u: UnitIn) => postJson("/api/school/curriculum/units", u));
}

export function useUpdateUnit() {
  return useUnitMutation(async (u: UnitIn & { id: string }) => {
    const resp = await fetch(`/api/school/curriculum/units/${u.id}`, {
      method: "PUT",
      credentials: "include",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(u),
    });
    if (!resp.ok) throw new Error(`PUT unit → ${resp.status}`);
    return resp.json();
  });
}

export function useMarkTaught() {
  return useUnitMutation((id: string) => postJson(`/api/school/curriculum/units/${id}/mark-taught`, {}));
}

export function useUnmarkTaught() {
  return useUnitMutation((id: string) => postJson(`/api/school/curriculum/units/${id}/unmark`, {}));
}

export function useDeleteUnit() {
  return useUnitMutation(async (id: string) => {
    const resp = await fetch(`/api/school/curriculum/units/${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: authHeaders(),
    });
    if (!resp.ok) throw new Error(`DELETE unit → ${resp.status}`);
  });
}

// ── Exam Engine P1: blueprint ────────────────────────────────────────

export interface BlueprintCell {
  kp_code: string;
  item_type: string;
  count: number;
  score_each: number;
  difficulty?: number;
  section_axis: string;
}

export interface Blueprint {
  id: string;
  exam_id: string;
  version: number;
  matrix: BlueprintCell[];
  scope_explanation: string;
  approved_at: string | null;
}

export interface TaughtKp {
  kp_id: string;
  code: string;
  name_zh: string;
  name_en: string | null;
  axis: string | null;
  last_taught: string;
  emphasis: number;
  units: string[];
}

export interface BlueprintBundle {
  exam: { id: string; class_id: string | null; subject_id?: string | null; title: string; type: string; exam_date: string; max_total: number; class_name: string | null };
  taught_scope: TaughtKp[];
  blueprint: Blueprint | null;
  /** code → name_zh for every KP in the matrix (incl. candidate/topic KPs). */
  kp_names?: Record<string, string>;
}

export function useBlueprint(examId: string | undefined) {
  return useQuery({
    queryKey: ["school", "blueprint", examId ?? ""],
    queryFn: () => getJson<BlueprintBundle>(`/api/school/exams/${examId}/blueprint`),
    enabled: !!examId,
  });
}

export function useInferBlueprint(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { add_kp_ids: string[]; remove_kp_ids: string[]; notes: string; topics?: string[] }) =>
      postJson<Blueprint & { computed_total: number; stripped_kps: string[]; kp_names?: Record<string, string> }>(
        `/api/school/exams/${examId}/blueprint/infer`,
        p,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "blueprint", examId] }),
  });
}

export function useApproveBlueprint(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { matrix?: BlueprintCell[] }) =>
      postJson<Blueprint>(`/api/school/exams/${examId}/blueprint/approve`, p),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "blueprint", examId] }),
  });
}

// ── Exam Engine P1: paper assembly ───────────────────────────────────

export interface PaperItem {
  id: string;
  paper_item_id: string;
  seq: number;
  section_label: string | null;
  section_axis: string | null;
  item_type: string;
  stem: string;
  options: { key: string; text: string }[] | null;
  answer_key: Record<string, unknown> | null;
  rubric: { band: string; criteria: string }[] | null;
  difficulty: number | null;
  score: number;
  source: string;
}

export interface PaperBundle {
  id?: string;
  exam_id: string;
  status: "none" | "draft" | "in_review" | "finalized" | "printed";
  version?: number;
  total?: number;
  items: PaperItem[];
  versions: { version: number; change_summary: string | null; created_at: string }[];
  reused?: number;
  generated?: number;
  cell_failures?: string[];
  warning?: string | null;
}

export function usePaper(examId: string | undefined) {
  return useQuery({
    queryKey: ["school", "paper", examId ?? ""],
    queryFn: () => getJson<PaperBundle>(`/api/school/exams/${examId}/paper`),
    enabled: !!examId,
  });
}

function usePaperMutation<T>(examId: string, path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: T) => postJson<PaperBundle>(`/api/school/exams/${examId}/paper/${path}`, p ?? {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "paper", examId] }),
  });
}

export const useGeneratePaper = (examId: string) => usePaperMutation<{ notes: string }>(examId, "generate");
export const useRevisePaper = (examId: string) =>
  usePaperMutation<{ regenerate_seqs: number[]; remove_seqs: number[]; instruction: string; add?: object }>(examId, "revise");
export const useFinalizePaper = (examId: string) => usePaperMutation<object>(examId, "finalize");
export const useUnlockPaper = (examId: string) => usePaperMutation<object>(examId, "unlock");

export function useRollbackPaper(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (version: number) => postJson<PaperBundle>(`/api/school/exams/${examId}/paper/rollback/${version}`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "paper", examId] }),
  });
}

// ── Exam Engine P1-S3: named printouts ───────────────────────────────

export interface PrintoutRecord {
  student_id: string;
  name_zh: string;
  paper_version: number;
  generated_at: string;
  reprint_reason: string | null;
}

export function usePrintouts(examId: string | undefined) {
  return useQuery({
    queryKey: ["school", "printouts", examId ?? ""],
    queryFn: () => getJson<{ printouts: PrintoutRecord[] }>(`/api/school/exams/${examId}/paper/printouts`),
    enabled: !!examId,
  });
}

export interface PrintoutsResult {
  download: string;
  students: number;
  missing_codes: string[];
  reprinted: string[];
  paper_version: number;
}

export function useGeneratePrintouts(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { student_ids?: string[]; allow_missing_codes: boolean; reprint_reason?: string }) => {
      const resp = await fetch(`/api/school/exams/${examId}/paper/printouts`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`printouts ${resp.status}`), { detail: body?.detail });
      return body as PrintoutsResult;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["school", "printouts", examId] });
      void qc.invalidateQueries({ queryKey: ["school", "paper", examId] });
    },
  });
}

// ── Exam Engine P1-S4: item-score grid + attribution ─────────────────

export interface GridItem {
  item_id: string;
  seq: number;
  section_label: string | null;
  section_axis: string | null;
  item_type: string;
  max: number;
  objective: boolean;
}

export interface GridStudent {
  id: string;
  name: string;
  name_en: string | null;
  student_code: string | null;
}

export interface GridCell {
  score: number | null;
  is_correct: boolean | null;
}

export interface ScoreGridBundle {
  exam: { id: string; title: string; class_id: string; class_name: string; exam_date: string; max_total: number };
  paper: { id: string; version: number; status: string };
  items: GridItem[];
  students: GridStudent[];
  cells: Record<string, Record<string, GridCell>>;
  absent: string[];
  attributed: string[];
}

export function useScoreGrid(examId: string | undefined) {
  return useQuery({
    queryKey: ["school", "score-grid", examId ?? ""],
    queryFn: () => getJson<ScoreGridBundle>(`/api/school/exams/${examId}/item-scores`),
    enabled: !!examId,
  });
}

export function usePutScoreCell(examId: string) {
  return useMutation({
    mutationFn: async (p: { student_id: string; item_id: string; score?: number | null; is_correct?: boolean | null }) => {
      const resp = await fetch(`/api/school/exams/${examId}/item-scores/cell`, {
        method: "PUT",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`cell ${resp.status}`), { detail: body?.detail });
      return body as { ok: boolean; score: number | null; is_correct: boolean | null };
    },
  });
}

export function useMarkAbsent(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { student_id: string; is_absent: boolean }) =>
      postJson<{ ok: boolean }>(`/api/school/exams/${examId}/item-scores/absent`, p),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "score-grid", examId] }),
  });
}

export interface AttributeResult {
  attributed: number;
  absent: number;
  skipped_incomplete: string[];
  objective_defaults_filled: number;
  kp_mastery_upserts: number;
}

export function useAttributeScores(examId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { allow_partial: boolean }) => {
      const resp = await fetch(`/api/school/exams/${examId}/item-scores/attribute`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`attribute ${resp.status}`), { detail: body?.detail });
      return body as AttributeResult;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "score-grid", examId] }),
  });
}

// ── Exam Engine P1-S5: KP-granularity class heatmap ──────────────────

export interface KpHeatmapCell {
  mastery: number | null;
  evidence_count: number;
  low_evidence: boolean;
  trend: "improving" | "stable" | "declining" | null;
}

export interface KpHeatmap {
  evidence_threshold: number;
  kps: { kp_id: string; code: string | null; name_zh: string; axis: string | null; axis_zh: string | null }[];
  students: { student_id: string; name_zh: string; name_en: string | null }[];
  cells: Record<string, Record<string, KpHeatmapCell>>;
}

export function useClassKpHeatmap(classId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["school", "stats", "class", classId, "kp-heatmap"],
    queryFn: () => getJson<KpHeatmap>(`/api/school/stats/class/${classId}/kp-heatmap`),
    enabled: !!classId && enabled,
  });
}

// ── Exam Engine pipeline status for /exams list rows ─────────────────

export interface ExamEngineStatus {
  blueprint: "none" | "draft" | "approved";
  paper: "none" | "draft" | "in_review" | "finalized" | "printed";
  printed: boolean;
  scored: number;
}

export function useExamEngineStatus(classId: string | undefined) {
  return useQuery({
    queryKey: ["school", "exams", classId, "engine-status"],
    queryFn: () => getJson<Record<string, ExamEngineStatus>>(`/api/school/exams/engine-status?class_id=${classId}`),
    enabled: !!classId,
  });
}

// ── Exam Engine E5: KP aggregate stats (weakest-first, server-computed) ──

export interface KpStatRow {
  kp_id: string;
  code: string | null;
  name_zh: string;
  axis: string | null;
  n_students: number;
  avg_mastery: number | null;
  evidence_total: number;
  low_evidence: boolean;
}

export function useKpStats(classId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["school", "stats", "kp", classId ?? ""],
    queryFn: () => getJson<{ kps: KpStatRow[] }>(`/api/school/stats/kp?class_id=${classId}`),
    enabled: !!classId && enabled,
  });
}

// ── Student lifecycle (spec-school-student-admin P0-a) ───────────────

export interface LifecycleRule {
  admissions_only?: boolean;
  required?: string[];
  label_zh?: string;
  effects_zh?: string;
}

export function useLifecycleTransitions() {
  return useQuery({
    queryKey: ["school", "lifecycle", "transitions"],
    queryFn: () =>
      getJson<{ legal: Record<string, Record<string, LifecycleRule>>; terminal: string[] }>(
        "/api/school/students/lifecycle/transitions",
      ),
    staleTime: 3600_000,
  });
}

export interface StatusChangeIn {
  to_status: string;
  reason?: string;
  effective_date?: string;
  expected_return_date?: string;
  transferred_to?: string;
}

export function useChangeStudentStatus(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: StatusChangeIn) => {
      const resp = await fetch(`/api/school/students/${studentId}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`status ${resp.status}`), { detail: body?.detail });
      return body as { ok: boolean; status: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["school", "student", studentId] });
      void qc.invalidateQueries({ queryKey: ["school", "students"] });
      void qc.invalidateQueries({ queryKey: ["school", "timeline", studentId] });
    },
  });
}

export function useReinstateStudent(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { class_id?: string; reason?: string }) =>
      postJson<{ ok: boolean }>(`/api/school/students/${studentId}/reinstate`, p),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["school", "student", studentId] });
      void qc.invalidateQueries({ queryKey: ["school", "timeline", studentId] });
    },
  });
}

export interface TimelineEvent {
  kind: "status" | "interview" | "transfer";
  from_school?: string;
  to_school?: string;
  at: string;
  from_status?: string;
  to_status?: string;
  reason?: string | null;
  effective_date?: string | null;
  expected_return_date?: string | null;
  transferred_to?: string | null;
  interview_date?: string;
  overall_score?: number | null;
  summary?: string;
}

export function useStudentTimeline(studentId: string | undefined) {
  return useQuery({
    queryKey: ["school", "timeline", studentId ?? ""],
    queryFn: () => getJson<TimelineEvent[]>(`/api/school/students/${studentId}/timeline`),
    enabled: !!studentId,
  });
}

// ── School detail (spec-school-student-admin P0-b) ───────────────────

export interface SchoolOverview {
  counts: {
    classes: number;
    teachers: number;
    active_students: number;
    on_leave: number;
    archived_students: number;
    teacherless_classes: number;
  };
  alerts: {
    teacherless_classes: { id: string; name: string }[];
    unassigned_active: number;
    overdue_on_leave: { id: string; name_zh: string; expected_return_date: string }[];
  };
}

export function useSchoolOverview(schoolId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "school", schoolId, "overview"],
    queryFn: () => getJson<SchoolOverview>(`/api/admin/schools/${schoolId}/overview`),
    enabled: !!schoolId,
  });
}

export interface SchoolClassRow {
  id: string; name: string; grade: string; semester: string;
  capacity: number; student_count: number; teachers: string[];
}

export function useSchoolClasses(schoolId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "school", schoolId, "classes"],
    queryFn: () => getJson<SchoolClassRow[]>(`/api/admin/schools/${schoolId}/classes`),
    enabled: !!schoolId,
  });
}

export interface SchoolTeacherRow {
  id: string; nova_user_id: string; name: string; email: string | null; role: string; classes: string[];
}

export function useSchoolTeachers(schoolId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "school", schoolId, "teachers"],
    queryFn: () => getJson<SchoolTeacherRow[]>(`/api/admin/schools/${schoolId}/teachers`),
    enabled: !!schoolId,
  });
}

export interface SchoolStudentRow {
  id: string; name_zh: string; name_en: string | null; student_code: string | null;
  grade: string; status: string; class_name: string | null;
  expected_return_date: string | null; status_reason: string | null; effective_date: string | null;
}

export function useSchoolStudentsAdmin(
  schoolId: string | undefined,
  opts: { archived: boolean; status?: string; class_id?: string; q?: string; page: number },
) {
  const params = new URLSearchParams();
  params.set("archived", String(opts.archived));
  params.set("page", String(opts.page));
  if (opts.status) params.set("status", opts.status);
  if (opts.class_id) params.set("class_id", opts.class_id);
  if (opts.q) params.set("q", opts.q);
  return useQuery({
    queryKey: ["admin", "school", schoolId, "students", params.toString()],
    queryFn: () =>
      getJson<{ total: number; page: number; per_page: number; students: SchoolStudentRow[] }>(
        `/api/admin/schools/${schoolId}/students?${params.toString()}`,
      ),
    enabled: !!schoolId,
    placeholderData: (prev) => prev,
  });
}

// ── Batch transfers (spec-school-student-admin P0-c) ─────────────────

export interface PreflightResult {
  can_proceed: boolean;
  requires_confirmation: boolean;
  blockers: { code: string; message: string }[];
  warnings: { code: string; message: string; affected?: string[] }[];
  info: {
    records_following: Record<string, number>;
    profile_frozen: boolean;
    in_flight: Record<string, number>;
    on_leave_in_batch: number;
  };
}

export function usePreflightTransfer() {
  return useMutation({
    mutationFn: (p: { student_ids: string[]; to_school_id: string; to_class_id: string }) =>
      postJson<PreflightResult>("/api/school/transfers/preflight", p),
  });
}

export interface TransferResult {
  batch_id: string;
  succeeded: number;
  failed: number;
  results: { student_id: string; ok: boolean; integrity_check?: string; error?: string; message?: string }[];
}

export function useExecuteTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      student_ids: string[]; to_school_id: string; to_class_id: string;
      effective_date: string; reason: string; acknowledged_warnings: string[];
    }) => {
      const resp = await fetch("/api/school/transfers/students", {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`transfer ${resp.status}`), { detail: body?.detail });
      return body as TransferResult;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "school"] }),
  });
}

// ── Teacher invitations (teacher-role spec M1) ───────────────────────

export interface Invitation {
  id: string;
  email: string;
  name: string | null;
  role: string;
  subject_ids: string[];
  status: "pending" | "accepted" | "expired" | "revoked";
  invite_url?: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export function useInvitations(status?: string) {
  return useQuery({
    queryKey: ["school", "invitations", status ?? ""],
    queryFn: () =>
      getJson<Invitation[]>(`/api/school/invitations${status ? `?status=${status}` : ""}`),
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      email: string; name?: string; role?: string;
      subject_ids: string[]; initial_password?: string;
    }) => {
      const resp = await fetch("/api/school/invitations", {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw Object.assign(new Error(`invite ${resp.status}`), { detail: body?.detail });
      return body as Invitation & { provisioned?: { user_id: string } };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "invitations"] }),
  });
}

export function useBulkInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch("/api/school/invitations/bulk", {
        method: "POST", credentials: "include", headers: authHeaders(), body: fd,
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(`bulk ${resp.status}`);
      return body as { succeeded: number; failed: number;
        results: { line: number; email: string; ok: boolean; error?: string; invite_url?: string }[] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "invitations"] }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => postJson<Invitation>(`/api/school/invitations/${id}/resend`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "invitations"] }),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const resp = await fetch(`/api/school/invitations/${id}`, {
        method: "DELETE", credentials: "include", headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`revoke ${resp.status}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "invitations"] }),
  });
}

export function useAckDataUse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => postJson<{ ok: boolean }>("/api/admin/org/ack-data-use", {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "org", "me"] }),
  });
}

export function usePatchSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id: string; patch: Record<string, unknown> }) => {
      const resp = await fetch(`/api/admin/subjects/${p.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(p.patch),
      });
      if (!resp.ok) throw new Error(`patch subject ${resp.status}`);
      return resp.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["subjects"] }),
  });
}

export function useArchiveSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; unarchive?: boolean }) =>
      postJson<{ ok: boolean }>(`/api/admin/subjects/${p.id}/${p.unarchive ? "unarchive" : "archive"}`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["subjects"] }),
  });
}

export interface SubjectRow {
  /** 启用的学科层级(高→低);空=不分层 */
  levels?: string[];
  id: string;
  slug: string;
  name: string;
  role_type?: string;
  status?: string;
  member_count?: number;
}

/** 学生的学科分层(详情页编辑用)。 */
export function useStudentLevels(studentId: string | undefined) {
  return useQuery({
    queryKey: ["student-levels", studentId],
    queryFn: () => getJson<{ subject_id: string; subject_name: string; slug: string; level: string }[]>(
      `/api/school/students/${studentId}/levels`),
    enabled: !!studentId,
  });
}

export function useSetStudentLevel(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { subject_id: string; level: string | null }) =>
      putJson<void>(`/api/school/students/${studentId}/levels`, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["student-levels", studentId] });
      void qc.invalidateQueries({ queryKey: ["school", "students"] });
      void qc.invalidateQueries({ queryKey: ["class-roster"] });
    },
  });
}

export function useSubjects() {
  return useQuery({
    queryKey: ["subjects"],
    queryFn: () => getJson<SubjectRow[]>("/api/admin/subjects"),
  });
}

export function useCreateSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; name: string; description?: string }) =>
      postJson<SubjectRow>("/api/admin/subjects", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subjects"] });
      qc.invalidateQueries({ queryKey: ["admin-subjects"] });
    },
  });
}


// ── AI Employees (teacher-role spec M3) ──────────────────────────────

export interface EmployeeRow {
  subject_id: string;
  slug: string;
  name: string;
  role_type: string;
  agent_id: string;
  registered: boolean;
  custom_prompt: string | null;
  prefs: Record<string, string>;
}

export function useEmployees() {
  return useQuery({
    queryKey: ["school", "employees"],
    queryFn: () => getJson<EmployeeRow[]>("/api/school/employees"),
    staleTime: 60_000,
  });
}

export function usePutEmployeePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { subject_id: string; custom_prompt?: string | null; prefs: Record<string, string> }) => {
      const resp = await fetch(`/api/school/employees/${p.subject_id}/prefs`, {
        method: "PUT",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ custom_prompt: p.custom_prompt, prefs: p.prefs }),
      });
      if (!resp.ok) throw new Error(`prefs ${resp.status}`);
      return resp.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["school", "employees"] }),
  });
}
